import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, User, Zap, TrendingUp, ShoppingBag, Sparkles, Truck, Globe, Heart, Baby, Gem } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { SEOLink } from '../seo/SEOLink';
import { SALES_TEAM } from '../../data/salesTeam';

// Services dropdown data organized by category
const servicesDropdown = {
  fashion: {
    title: "Fashion",
    icon: ShoppingBag,
    items: [
      { name: "Myntra", slug: "myntra" },
      { name: "Nykaa Fashion", slug: "nykaa-fashion" },
      { name: "Ajio", slug: "ajio" },
      { name: "Tata CliQ", slug: "tata-cliq" },
      { name: "Pernia's Pop-Up Shop", slug: "pernias-pop-up-shop" },
      { name: "Aza Fashions", slug: "aza-fashions" },
      { name: "Kalki Fashion", slug: "kalki-fashion" },
      { name: "Mirraw", slug: "mirraw" },
    ]
  },
  beauty: {
    title: "Beauty",
    icon: Sparkles,
    items: [
      { name: "Nykaa Beauty", slug: "nykaa" },
      { name: "Tira Beauty", slug: "tira" },
      { name: "Purplle", slug: "purple" },
      { name: "Smytten", slug: "smytten" },
      { name: "TataCliQ Palette", slug: "tata-cliq-palette" },
    ]
  },
  quickCommerce: {
    title: "Quick Commerce",
    icon: Truck,
    items: [
      { name: "Blinkit", slug: "blinkit" },
      { name: "Swiggy Instamart", slug: "swiggy-instamart" },
      { name: "Zepto", slug: "zepto" },
      { name: "BigBasket", slug: "bigbasket" },
    ]
  },
  health: {
    title: "Kids & Baby",
    icon: Heart,
    items: [
      { name: "Kids Brand Pillar", directPath: "/kids-brand-marketplace-onboarding-india", featured: true },
      { name: "FirstCry", directPath: "/firstcry" },
      { name: "Myntra Kids", directPath: "/myntra-kids-brand-onboarding" },
      { name: "Tata 1mg", slug: "tata-1mg" },
      { name: "Healthmug", slug: "healthmug" },
      { name: "Hopscotch", slug: "hopscotch" },
    ]
  },
  international: {
    title: "International",
    icon: Globe,
    items: [
      { name: "Noon (UAE)", slug: "noon" },
      { name: "Amazon (USA)", slug: "amazon-usa" },
      { name: "Zalando (EU)", slug: "zalando" },
    ]
  },
  luxury: {
    title: "Luxury",
    icon: Gem,
    items: [
      { name: "Myntra Luxe", slug: "myntra" },
      { name: "Ajio Luxe", slug: "ajio" },
      { name: "TataCliQ Luxury", slug: "tata-cliq-luxury" },
      { name: "Mirraw Luxe", slug: "mirraw" },
    ]
  }
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileCTA, setShowMobileCTA] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);
      // Show mobile CTA after scrolling 300px
      setShowMobileCTA(scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      dropdown: 'services'
    },
    { 
      name: 'Pricing', 
      path: '/products',
      dropdown: [
        { name: 'ABT Plans', path: '/products?tab=abt', description: 'One-time Onboarding', icon: Zap, color: 'text-blue-500' },
        { name: 'ABR Plans', path: '/products?tab=abr', description: '12-Month Management', icon: TrendingUp, color: 'text-green-500' },
        { name: 'Compare Plans', path: '/products#comparison', description: 'ABT vs ABR', icon: null, color: 'text-red-500' },
      ]
    },
    { name: 'JiniStudio', path: '/jinistudio' },
    { name: 'Zaroori Jini OMS', path: '/zaroori-jini-oms' },
    { name: 'Portfolio', path: '/success-stories' },
    { name: 'Blog', path: '/blog' },
    { name: 'Resources', path: '/resources' },
    { name: 'Life@ZR', path: '/life-at-zaroori' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white py-2.5 px-4 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <span className="text-slate-400">
            Office Addresses: <span className="text-white font-medium">Ludhiana | Noida | Dubai</span>
          </span>
          <div className="flex items-center gap-4" data-testid="topbar-sales-team">
            <span className="text-slate-400">Sales Team:</span>
            {SALES_TEAM.map((person) => (
              <a
                key={person.tel}
                href={`tel:${person.tel}`}
                data-testid={`topbar-sales-${person.tel}`}
                className="text-[#ef4444] hover:text-[#f87171] transition-colors font-medium"
              >
                <span className="text-slate-300 font-normal mr-1">{person.name}</span>
                {person.display}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main Navbar - Glassmorphism */}
      <nav className={cn(
        "sticky top-0 z-50 transition-all duration-500",
        isScrolled 
          ? "glass-strong shadow-lg" 
          : "bg-white/80 backdrop-blur-sm"
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <SEOLink to="/" className="flex items-center gap-3 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_49e3f0dc-64cb-41b7-891b-65258ccb235b/artifacts/4zp8z6bt_ZR%20landscape%20logo.png"
                alt="Zaroori Retail - Premium, Prolific, Presence"
                className="h-16 w-auto object-contain group-hover:scale-105 transition-transform"
                width="180"
                height="64"
                fetchPriority="high"
              />
            </SEOLink>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0.5" role="navigation" aria-label="Main navigation">
              {navLinks.map((link) => (
                <div 
                  key={link.name} 
                  className="relative"
                  onMouseEnter={() => {
                    if (link.name === 'Services') setServicesOpen(true);
                    if (link.name === 'Pricing') setProductsOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (link.name === 'Services') setServicesOpen(false);
                    if (link.name === 'Pricing') setProductsOpen(false);
                  }}
                >
                  {link.dropdown ? (
                    <button
                      className={cn(
                        "px-3 py-2 rounded-lg text-[13px] font-medium flex items-center gap-0.5 transition-all whitespace-nowrap",
                        location.pathname.includes(link.path) 
                          ? "text-[#ef4444] bg-red-50" 
                          : "text-slate-700 hover:text-[#ef4444] hover:bg-slate-50"
                      )}
                    >
                      {link.name}
                      <ChevronDown className={cn(
                        "w-3.5 h-3.5 transition-transform duration-200",
                        (link.name === 'Services' && servicesOpen) || (link.name === 'Pricing' && productsOpen) 
                          ? "rotate-180" : ""
                      )} />
                    </button>
                  ) : link.external ? (
                    <a
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "px-3 py-2 rounded-lg text-[13px] font-medium transition-all block whitespace-nowrap inline-flex items-center gap-1",
                        "text-slate-700 hover:text-[#ef4444] hover:bg-slate-50"
                      )}
                      data-testid={`nav-external-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                      <span className="text-[10px] opacity-60" aria-hidden>↗</span>
                    </a>
                  ) : (
                    <SEOLink
                      to={link.path}
                      className={cn(
                        "px-3 py-2 rounded-lg text-[13px] font-medium transition-all block whitespace-nowrap",
                        location.pathname === link.path 
                          ? "text-[#ef4444] bg-red-50" 
                          : "text-slate-700 hover:text-[#ef4444] hover:bg-slate-50"
                      )}
                    >
                      {link.name}
                    </SEOLink>
                  )}
                  
                  {/* Services Mega Dropdown */}
                  {link.name === 'Services' && link.dropdown === 'services' && servicesOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                      <div className="w-[900px] bg-white rounded-2xl p-6 shadow-2xl border border-gray-100">
                        <div className="grid grid-cols-6 gap-6">
                          {Object.values(servicesDropdown).map((category) => {
                            const Icon = category.icon;
                            return (
                              <div key={category.title}>
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                                  <Icon className="w-4 h-4 text-red-500" />
                                  <span className="font-semibold text-gray-800 text-sm">{category.title}</span>
                                </div>
                                <div className="space-y-1">
                                  {category.items.map((item) => (
                                    <SEOLink
                                      key={item.slug || item.directPath}
                                      to={item.directPath || `/services/${item.slug}`}
                                      className={`block px-2 py-1.5 text-xs rounded-lg transition-colors ${item.featured ? 'text-red-600 font-bold bg-red-50 hover:bg-red-100 hover:text-red-700' : 'text-gray-600 hover:text-red-500 hover:bg-red-50'}`}
                                    >
                                      {item.featured && <span className="mr-1">★</span>}{item.name}
                                    </SEOLink>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                          <SEOLink to="/services" className="text-red-500 text-sm font-medium hover:underline">
                            View All 35+ Marketplaces →
                          </SEOLink>
                          <span className="text-xs text-gray-400">India • UAE • USA • Europe • SEA</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Pricing Dropdown */}
                  {link.name === 'Pricing' && link.dropdown && productsOpen && (
                    <div className="absolute top-full left-0 pt-2">
                      <div className="w-64 bg-white rounded-2xl py-3 shadow-xl border border-gray-100">
                        {link.dropdown.map((item) => {
                          const Icon = item.icon;
                          return (
                            <SEOLink
                              key={item.name}
                              to={item.path}
                              className="flex items-center gap-3 px-4 py-3 text-sm text-slate-600 hover:bg-red-50 hover:text-[#ef4444] transition-colors mx-2 rounded-xl"
                            >
                              {Icon && (
                                <div className={`w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center ${item.color}`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium">{item.name}</p>
                                {item.description && (
                                  <p className="text-xs text-gray-400">{item.description}</p>
                                )}
                              </div>
                            </SEOLink>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side - CTA Buttons */}
            <div className="hidden lg:flex items-center gap-2">
              <Button 
                onClick={() => {
                  const event = new CustomEvent('openDemoModal');
                  window.dispatchEvent(event);
                }}
                variant="outline"
                className="rounded-full px-5 h-10 text-sm font-semibold border-2 border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white transition-all"
                data-testid="navbar-demo-btn"
              >
                Book Demo
              </Button>
              <SEOLink to="/contact">
                <Button className="bg-gradient-to-r from-[#ef4444] to-[#dc2626] hover:from-[#dc2626] hover:to-[#b91c1c] text-white rounded-full px-5 h-10 text-sm font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] transition-all">
                  Get Quote
                  <span className="ml-1.5">→</span>
                </Button>
              </SEOLink>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2.5 neo-shadow-sm rounded-xl bg-white"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Glassmorphism */}
        {isMobileMenuOpen && (
          <div className="lg:hidden glass-strong border-t border-white/20 py-4">
            <div className="px-4 space-y-2">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.name}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-white/50 hover:text-[#ef4444] transition-colors"
                    data-testid={`mobile-nav-external-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name} <span className="text-xs opacity-60" aria-hidden>↗</span>
                  </a>
                ) : (
                  <SEOLink
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-white/50 hover:text-[#ef4444] transition-colors"
                  >
                    {link.name}
                  </SEOLink>
                )
              ))}
              <SEOLink to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-gradient-to-r from-[#ef4444] to-[#dc2626] text-white rounded-2xl mt-4 neo-shadow-sm">
                  GET A QUOTE
                </Button>
              </SEOLink>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Sticky CTA Bar */}
      <div 
        className={cn(
          "fixed bottom-0 left-0 right-0 z-50 lg:hidden transition-transform duration-300 ease-out",
          showMobileCTA && !isMobileMenuOpen ? "translate-y-0" : "translate-y-full"
        )}
      >
        <div className="bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] px-4 py-3 safe-area-bottom">
          <div className="flex items-center gap-3 max-w-md mx-auto">
            <Button 
              onClick={() => {
                const event = new CustomEvent('openDemoModal');
                window.dispatchEvent(event);
              }}
              variant="outline"
              className="flex-1 rounded-full h-11 text-sm font-semibold border-2 border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white transition-all"
              data-testid="mobile-cta-demo-btn"
            >
              Book Demo
            </Button>
            <SEOLink to="/contact" className="flex-1">
              <Button className="w-full bg-gradient-to-r from-[#ef4444] to-[#dc2626] hover:from-[#dc2626] hover:to-[#b91c1c] text-white rounded-full h-11 text-sm font-semibold shadow-md">
                Get Quote →
              </Button>
            </SEOLink>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
